import { useEffect, useRef } from 'react';
import { cropOptions } from '../../../mocks/roiDefaults';
import type { CalculatorInputs } from './CalculatorForm';
import CumulativeSavingsChart from './CumulativeSavingsChart';

interface ResultData {
  annualSavings: number;
  roiPercent: number;
  yieldRecovered: number;
  compactionSaved: number;
  timingSaved: number;
  deerSaved: number;
  assessmentSaved: number;
  serviceCost: number;
  netServiceCost: number;
  grossRevenueCurrent: number;
  grossRevenueWithService: number;
}

/**
 * Calculates all ROI related numbers.
 * FIXED: All critical calculation errors corrected based on audit.
 */
function calculateResults(inputs: CalculatorInputs): ResultData {
  // Guard against missing or malformed inputs
  const acreage = inputs.acreage > 0 ? inputs.acreage : 0;
  const applications = inputs.applicationsPerSeason > 0 ? inputs.applicationsPerSeason : 0;

  const crop = cropOptions.find(c => c.value === inputs.cropType) || cropOptions[0];
  const grossPerAcre = crop.yieldPerAcre * crop.pricePerBushel;
  const grossRevenueCurrent = grossPerAcre * acreage;

  let compactionSaved = 0;
  let timingSaved = 0;
  let deerSaved = 0;
  let assessmentSaved = 0;

  // FIX #1: Compaction savings apply when switching FROM any ground-based method TO drones
  if (inputs.selectedServices.includes('aerial') && 
      ['ground', 'aerial-plane', 'manual'].includes(inputs.currentMethod)) {
    compactionSaved = grossPerAcre * 0.04 * acreage;
  }

  // FIX #2: Timing savings based on critical spray windows (typically 1-2 per season)
  if (inputs.selectedServices.includes('timing') && inputs.currentMethod !== 'none' && applications > 0) {
    const criticalWindows = Math.min(applications, 2); // Cap at 2 critical windows per season
    timingSaved = grossPerAcre * 0.08 * acreage * (criticalWindows / 2);
  }

  // FIX #3: Deer deterrent is a seasonal program (flat cost), not per-application
  if (inputs.selectedServices.includes('deer')) {
    const edgeAcres = acreage * 0.08;
    deerSaved = edgeAcres * 150;
  }

  // FIX #4: Assessment savings assume 25% of fields have economically fixable issues
  if (inputs.selectedServices.includes('assessment')) {
    assessmentSaved = acreage * 0.25 * 65;
  }

  const annualSavings = compactionSaved + timingSaved + deerSaved + assessmentSaved;

  // Calculate service costs
  const aerialCost = inputs.selectedServices.includes('aerial')
    ? acreage * 12 * applications
    : 0;
  
  // FIX #3 continued: Deer cost is now flat seasonal cost, not per-application
  const deerCost = inputs.selectedServices.includes('deer')
    ? acreage * 0.08 * 150
    : 0;
  
  const assessmentCost = inputs.selectedServices.includes('assessment')
    ? acreage * 3.5
    : 0;
  
  const timingCost = 0; // Timing is included in aerial application cost
  const totalServiceCost = aerialCost + deerCost + assessmentCost + timingCost;

  // FIX #5: Subtract current method baseline cost
  const currentMethodCostPerAcre: Record<string, number> = {
    'ground': 8,
    'aerial-plane': 18,
    'manual': 25,
    'none': 0,
  };
  
  const currentMethodCost = (currentMethodCostPerAcre[inputs.currentMethod] || 0) * acreage * applications;
  const netServiceCost = totalServiceCost - currentMethodCost;

  const netSavings = annualSavings - netServiceCost;
  
  // FIX: Handle division by zero when netServiceCost is 0 or negative
  const roiPercent = netServiceCost > 0 ? (netSavings / netServiceCost) * 100 : 0;

  const pricePerBushel = crop.pricePerBushel || 1;
  const yieldRecovered = (compactionSaved + timingSaved) / pricePerBushel;

  return {
    annualSavings: Math.round(annualSavings),
    roiPercent: Math.round(roiPercent),
    yieldRecovered: Math.round(yieldRecovered),
    compactionSaved: Math.round(compactionSaved),
    timingSaved: Math.round(timingSaved),
    deerSaved: Math.round(deerSaved),
    assessmentSaved: Math.round(assessmentSaved),
    serviceCost: Math.round(totalServiceCost),
    netServiceCost: Math.round(netServiceCost),
    grossRevenueCurrent: Math.round(grossRevenueCurrent),
    grossRevenueWithService: Math.round(grossRevenueCurrent + annualSavings),
  };
}

/**
 * Formats a number as USD currency without decimal places.
 */
function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

interface Props {
  inputs: CalculatorInputs;
}

/**
 * ResultsPanel – displays a summary of calculated ROI data.
 * Includes a smooth scroll‑into‑view effect when inputs change.
 */
export default function ResultsPanel({ inputs }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const r = calculateResults(inputs);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [inputs]);

  const netSavings = r.annualSavings - r.netServiceCost;

  // Build breakdown items, filtering out zero‑value entries
  const breakdownItems = [
    { label: 'Compaction & Wheel Traffic Savings', value: r.compactionSaved, icon: 'ri-plant-line', type: 'recurring' },
    { label: 'Timely Application Yield Recovery', value: r.timingSaved, icon: 'ri-time-line', type: 'recurring' },
    { label: 'Deer Damage Prevention (Seasonal)', value: r.deerSaved, icon: 'ri-shield-check-line', type: 'seasonal' },
    { label: 'Drainage & Stress Detection (One-Time)', value: r.assessmentSaved, icon: 'ri-eye-line', type: 'seasonal' },
  ].filter(item => item.value > 0);

  // Guard against empty breakdown to avoid Math.max of an empty array
  const maxVal = breakdownItems.length ? Math.max(...breakdownItems.map(i => i.value), 1) : 1;

  // Helper to safely compute per‑acre values
  const perAcre = (value: number) => (inputs.acreage > 0 ? Math.round(value / inputs.acreage) : 0);

  return (
    <div ref={ref} className="scroll-mt-32">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-emerald-50 rounded-lg p-6 text-center">
          <div className="text-3xl md:text-4xl font-bold text-emerald-800 mb-1">
            {formatCurrency(r.annualSavings)}
          </div>
          <p className="text-sm text-gray-600 font-medium">Estimated Annual Recovery</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-6 text-center">
          <div className="text-3xl md:text-4xl font-bold text-emerald-800 mb-1">{r.roiPercent}%</div>
          <p className="text-sm text-gray-600 font-medium">Return on Investment</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-6 text-center">
          <div className="text-3xl md:text-4xl font-bold text-emerald-800 mb-1">
            {formatCurrency(netSavings)}
          </div>
          <p className="text-sm text-gray-600 font-medium">Net Savings After Service Cost</p>
        </div>
      </div>

      {/* Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-5">Savings Breakdown</h3>
        <div className="space-y-4">
          {breakdownItems.map(item => (
            <div key={item.label}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-emerald-700`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                </div>
                <span className="text-sm font-bold text-emerald-800">
                  {formatCurrency(item.value)}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className="bg-emerald-600 h-2.5 rounded-full transition-all duration-700"
                  style={{ width: `${(item.value / maxVal) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-gray-100 grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Net Additional Cost</p>
            <p className="text-xl font-bold text-gray-800">{formatCurrency(r.netServiceCost)}</p>
            <p className="text-xs text-gray-400 mt-1">After current method credit</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs text-emerald-700 font-medium mb-1">Net Return to You</p>
            <p className="text-xl font-bold text-emerald-800">{formatCurrency(netSavings)}</p>
          </div>
        </div>
      </div>

      {/* Per‑Acre Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Per‑Acre Impact</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-stone-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(perAcre(r.annualSavings))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Recovered / Acre</p>
          </div>
          <div className="text-center p-4 bg-stone-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(perAcre(r.netServiceCost))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Net Cost / Acre</p>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-lg">
            <div className="text-2xl font-bold text-emerald-800">
              {formatCurrency(perAcre(netSavings))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Net Gain / Acre</p>
          </div>
        </div>
      </div>

      {/* Financing Comparison */}
      {r.serviceCost > 0 && (
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-900 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-bank-card-line text-emerald-300 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold">Monthly Payment vs. Monthly Savings</h3>
              <p className="text-emerald-200 text-sm">See how financing makes this service pay for itself</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Monthly Payment */}
            <div className="bg-white/10 rounded-lg p-5 text-center">
              <p className="text-emerald-300 text-xs font-semibold tracking-wide mb-2">EST. MONTHLY PAYMENT</p>
              <p className="text-3xl font-bold mb-1">
                {formatCurrency(Math.round(r.serviceCost / 12))}
              </p>
              <p className="text-emerald-200 text-xs">12-month financing</p>
            </div>

            {/* vs indicator */}
            <div className="flex items-center justify-center">
              <div className="bg-emerald-700 rounded-full px-4 py-2 flex items-center gap-2">
                <span className="text-emerald-200 text-sm font-medium">vs</span>
                <i className="ri-arrow-right-line text-emerald-300"></i>
              </div>
            </div>

            {/* Monthly Savings */}
            <div className="bg-emerald-600/30 rounded-lg p-5 text-center border border-emerald-500/30">
              <p className="text-emerald-300 text-xs font-semibold tracking-wide mb-2">YOUR MONTHLY SAVINGS</p>
              <p className="text-3xl font-bold mb-1">
                {formatCurrency(Math.round(r.annualSavings / 12))}
              </p>
              <p className="text-emerald-200 text-xs">Projected recovery</p>
            </div>
          </div>

          {/* Net Monthly Benefit */}
          {r.annualSavings > r.serviceCost && (
            <div className="bg-white rounded-lg p-4 mb-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full">
                    <i className="ri-arrow-up-circle-line text-emerald-700 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Net Monthly Benefit</p>
                    <p className="text-xs text-gray-500">Your savings exceed your payment</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-700">
                    +{formatCurrency(Math.round((r.annualSavings - r.serviceCost) / 12))}
                  </p>
                  <p className="text-xs text-gray-500">per month</p>
                </div>
              </div>
            </div>
          )}

          {/* 0% APR Highlight */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-emerald-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-400 rounded-full">
                <i className="ri-percent-line text-amber-900 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold">Ask about 0% APR promotional financing</p>
                <p className="text-emerald-200 text-xs">Qualified applicants may pay even less per month</p>
              </div>
            </div>
            <a
              href="tel:+17179131780"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-800 font-semibold rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-phone-line"></i>
              Call for Financing Details
            </a>
          </div>

          <p className="text-emerald-300/70 text-xs mt-4 text-center">
            Financing subject to credit approval. Payment estimate based on 12-month term. Contact us for complete terms and 0% APR qualification requirements.
          </p>
        </div>
      )}

      {/* Cumulative Savings Projection Charts */}
      <div className="mb-8">
        <CumulativeSavingsChart inputs={inputs} />
      </div>

      {/* FIX #6: Updated disclaimer with conservative, defensible language */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
            <i className="ri-information-line text-amber-600 text-lg"></i>
          </div>
          <div className="text-xs text-gray-700 leading-relaxed">
            <p className="font-semibold text-gray-800 mb-2">Important: These are estimates only</p>
            <p>
              Calculations based on USDA Mid-Atlantic yield data, university extension research on compaction losses (3-8% range), 
              and regional field observations. Actual results will vary significantly based on soil type, weather patterns, 
              field conditions, management practices, crop variety, and market prices. 
            </p>
            <p className="mt-2">
              <strong>Assumptions:</strong> Compaction savings assume repeated wheel traffic in wet conditions (4% yield loss). 
              Timing benefits assume 1-2 critical fungicide windows per season (8% yield protection). 
              Assessment savings assume 25% of fields have economically fixable drainage or stress issues. 
              Deer deterrent is a seasonal program cost.
            </p>
            <p className="mt-2 text-amber-800 font-medium">
              This calculator provides projections for planning purposes and does not guarantee specific financial outcomes.
            </p>
          </div>
        </div>
      </div>

      {/* Red Callout */}
      <div className="border-l-3 border-red-500 bg-red-50/60 rounded-r-lg px-5 py-4 flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
          <i className="ri-error-warning-line text-red-500 text-lg"></i>
        </div>
        <div>
          <p className="text-sm text-gray-700">
            Without action, your operation could be leaving{' '}
            <strong className="text-red-600">{formatCurrency(r.annualSavings)}</strong> on the table
            this season alone. Over 5 years, that compounds to{' '}
            <strong className="text-red-600">{formatCurrency(r.annualSavings * 5)}</strong> in
            preventable losses.
          </p>
          <p className="text-sm text-emerald-700 font-medium mt-2">
            <i className="ri-shield-check-line mr-1"></i>
            We handle everything end‑to‑end — you focus on farming.
          </p>
        </div>
      </div>
    </div>
  );
}
